import React, { useState } from "react";
import { Container, Row, Col, Modal, Carousel } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { TfiPlus } from "react-icons/tfi";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BsArrowLeftCircle } from "react-icons/bs";

import useAsync from "../../../../hooks/useAsync";
import { typesAPI, datesAPI, money, dateRange, daysNights } from "../../../../services/api";
import WaitlistForm from "../../../booking/WaitlistForm";
import "./productDetail.css";
import Picture from "../../../utils/Picture";

/* Shown under "What's included" until a retreat type has its own list
   (Admin → Retreat types → What's included). */
const whatsIncluded = [
  {
    title: "Wellness & Healing",
    items: ["Daily yoga and meditation sessions", "Access to spa treatments (massages, bodywork)", "Use of natural soaking baths and thermal-fed pools"],
  },
  {
    title: "Accommodation & Comfort",
    items: ["Private or shared cabins, suites, or chalets", "Rooms include private bathroom, balcony, kitchenette, fan or AC"],
  },
  {
    title: "Food & Nourishment",
    items: ["Daily plant-based or retreat-friendly meals (breakfast, lunch, dinner)", "Continental breakfast included", "Herbal teas and hydration available throughout"],
  },
  {
    title: "Nature & Amenities",
    items: ["Access to natural pools, lagoons, and gardens", "Scenic nature trails and outdoor gathering spaces", "Fire pit for group use and evening connection"],
  },
  {
    title: "Guest Support",
    items: ["Airport or local shuttle assistance (optional add-on)", "Wi-Fi in rooms and common areas", "Retreat coordination & event support", "Multilingual staff on-site"],
  },
];

const GALLERY_FALLBACK = ["/gallery/gal1.jpg", "/gallery/gal2.jpg", "/gallery/gal3.jpg", "/gallery/gal4.jpg"];

/**
 * One retreat type — Ayahuasca, Women's, Veterans… — with every date it
 * has open. ?date=<id> preselects a date (from the homepage cards and the
 * Upcoming Retreats list). An old link to a single dated retreat is sent
 * on to its type, with that date chosen.
 */
const ProductDetail = () => {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState(0);
  const navigate = useNavigate();

  const { data: retreat, loading, error } = useAsync(
    () => typesAPI.get(slug).catch(async (err) => {
      if (err.status !== 404) throw err;
      // A dated retreat's own address, from before retreats had types
      const { dates } = await datesAPI.list();
      const d = dates.find((x) => x.slug === slug || String(x.id) === slug);
      if (d && d.typeSlug && d.typeSlug !== slug) {
        navigate(`/retreats/${d.typeSlug}?date=${d.id}`, { replace: true });
        return null;
      }
      throw err;
    }),
    [slug],
  );

  if (loading) {
    return (
      // Same height as the hero that replaces it, so nothing below jumps when it arrives.
      <div className="grain-bg-light d-flex align-items-center justify-content-center" style={{ minHeight: "200dvh" }}>
        <p className="fs-3 text-primary-color">Loading…</p>
      </div>
    );
  }

  if (error || !retreat) {
    return (
      <div className="ch-100 grain-bg-light d-flex flex-column align-items-center justify-content-center gap-4 text-center px-4">
        <p className="fs-3 text-primary-color mb-0">
          {error?.status === 404 ? "We couldn't find that retreat." : "We couldn't load that retreat just now."}
        </p>
        <button className="blob-btn px-5 fs-5 py-3" onClick={() => navigate("/retreats")}>SEE ALL RETREATS</button>
      </div>
    );
  }

  const dates = retreat.dates || [];
  const wanted = Number(params.get("date")) || null;
  const selected = dates.find((d) => d.id === wanted) || dates.find((d) => !d.soldOut) || dates[0] || null;
  const choose = (d) => setParams({ date: String(d.id) }, { replace: true });

  const images = [retreat.heroImage, ...GALLERY_FALLBACK].filter(Boolean).slice(0, 6);
  const sections = [
    retreat.itinerary?.length ? { title: "Itinerary", items: retreat.itinerary } : null,
    ...(retreat.inclusions?.length ? [{ title: "What's included", items: retreat.inclusions }] : whatsIncluded),
    retreat.preparation ? { title: "Preparation and requirements", text: retreat.preparation } : null,
    retreat.travel ? { title: "Travel and arrival", text: retreat.travel } : null,
    retreat.eligibility ? { title: "Who this retreat is for", text: retreat.eligibility } : null,
  ].filter(Boolean);

  return (
    <>
      <div className="ch-100 grain-bg-light">
        <div className="h-100 position-relative">
          {/* The largest picture on the page: fetched first, never lazily. */}
          <Picture src={retreat.heroImage || GALLERY_FALLBACK[0]} className="prodHero" sizes="100vw" priority width={1600} height={900} />

          <button
            className="d-flex gap-2 border-0 ms-5 mb-2 position-absolute bottom-0 start-0 bg-none d-flex align-items-center"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeftCircle className="fs-1 me-2" />
            <span className="wSpacing pFont fw-bold lead"> GO BACK</span>
          </button>
        </div>
      </div>

      <div className="grain-bg-light ch-100 d-flex align-items-center px-5">
        <div className="px-xl-5 w-100">
          <Container fluid className="px-lg-5">
            <Row className="mt-5 align-items-start">
              {/* On a phone the dates and the Reserve button come first; the pictures follow. */}
              <Col md={6} className="order-2 order-md-1 mt-5 mt-md-0">
                <div className="main-img-wrapper">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeIndex}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.02 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                    >
                      <Picture src={images[activeIndex]} className="main-img" alt={`${retreat.name} — view ${activeIndex + 1}`}
                        sizes="(min-width: 768px) 45vw, 100vw" width={800} height={540} />
                    </motion.div>
                  </AnimatePresence>
                </div>

                <Row className="mt-3 g-2">
                  {images.slice(0, 3).map((img, idx) => (
                    <Col md={4} xs={4} key={img + idx}>
                      <div className="position-relative">
                        <motion.button type="button" className="thumb-btn" onClick={() => setActiveIndex(idx)}
                          aria-label={`Show picture ${idx + 1}`} aria-pressed={activeIndex === idx}
                          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                          <Picture src={img} className={`thumb-img ${activeIndex === idx ? "active" : ""}`} alt="" sizes="160px" width={200} height={150} />
                        </motion.button>
                        {idx === 2 && images.length > 3 && (
                          <div
                            className="toggler d-flex align-items-center justify-content-center"
                            onClick={() => setShow(true)}
                            role="button"
                            tabIndex={0}
                            aria-label="See all photos"
                            onKeyDown={(e) => { if (e.key === "Enter") setShow(true); }}
                          >
                            <FaPlus className="fs-1 text-secondary-color" />
                          </div>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>

                {retreat.addons?.length > 0 && (
                  <div className="rt-addons mt-5">
                    <h2 className="pFont fs-5 fw-bold wSpacing text-uppercase mb-3">Add to your stay</h2>
                    <p className="mk-muted">Chosen while you book. What's on offer can differ from one date to another.</p>
                    <ul>
                      {retreat.addons.filter((a) => a.inclusion !== "included").map((a) => (
                        <li key={a.id}>
                          <span>
                            <b>{a.name}</b>
                            {(a.duration || a.requiresClearance) && (
                              <small>{[a.duration, a.requiresClearance ? "needs medical clearance" : null].filter(Boolean).join(" · ")}</small>
                            )}
                          </span>
                          <span className="rt-addon-price">{a.inclusion === "request" ? "On request" : money(a.price)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Col>

              {/* RIGHT SECTION */}
              <Col md={6} className="ps-xl-5 order-1 order-md-2">
                <h1 className="text-start display-4 mt-0 text-uppercase">{retreat.name}</h1>
                {retreat.tagline && <p className="fs-4 text-primary-color mb-4">{retreat.tagline}</p>}

                {dates.length > 0 ? (
                  <section aria-labelledby="dates-title" className="rt-dates">
                    <h2 id="dates-title" className="fs-5 mb-3 fw-bold tabValue">DATES</h2>
                    <div className="rt-date-list" role="radiogroup" aria-labelledby="dates-title">
                      {dates.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          role="radio"
                          aria-checked={selected?.id === d.id}
                          className={`rt-date ${selected?.id === d.id ? "is-picked" : ""} ${d.soldOut ? "is-full" : ""}`}
                          onClick={() => choose(d)}
                        >
                          <span className="rt-date-when">{dateRange(d.startDate, d.endDate)}</span>
                          <span className="rt-date-len">{daysNights(d.nights)}</span>
                          <span className="rt-date-left">
                            {d.soldOut ? "Full" : `${d.placesLeft} ${d.placesLeft === 1 ? "space" : "spaces"} left`}
                          </span>
                        </button>
                      ))}
                    </div>

                    {selected && (
                      <div className="rt-selected">
                        <p className="rt-price">
                          From <b>{money(selected.priceFrom)}</b> per person
                          {selected.earlyDiscount && (
                            <span className="rt-early">
                              Early booking saves {money(selected.earlyDiscount.amount)} until {dateRange(selected.earlyDiscount.until)}
                            </span>
                          )}
                        </p>
                        {selected.location && <p className="rt-where">{selected.location}</p>}
                        {selected.soldOut ? (
                          selected.waitlist ? <WaitlistForm pathwayId={selected.id} /> : <p className="mk-muted">This date is full.</p>
                        ) : (
                          <button
                            className="btn bg-black rounded-0 border-0 fs-5 pFont fw-bold text-secondary-color wSpacing p-4 py-5 w-100 mt-3"
                            onClick={() => navigate(`/book/${selected.slug}`)}
                          >
                            RESERVE YOUR PLACE
                          </button>
                        )}
                      </div>
                    )}
                  </section>
                ) : (
                  <section id="notify" className="rt-dates">
                    <h2 className="fs-5 mb-3 fw-bold tabValue">DATES</h2>
                    <WaitlistForm typeId={retreat.id} />
                  </section>
                )}

                {retreat.description && (
                  <>
                    <h2 className="facilities-title pFont mt-5 mb-4 fw-bold text-dark-color fs-5">OVERVIEW</h2>
                    <p className="text-dark-color fs-5 rt-overview">{retreat.description}</p>
                  </>
                )}

                <div className="included-section mt-5 pt-4">
                  <div className="included-list">
                    {sections.map((section, idx) => {
                      const isOpen = openIndex === idx;
                      const panel = `rt-panel-${idx}`;
                      return (
                        <div key={section.title} className="included-item">
                          <button className="included-header" aria-expanded={isOpen} aria-controls={panel} onClick={() => setOpenIndex(isOpen ? null : idx)}>
                            <span className="pFont fs-3">{section.title}</span>
                            <motion.span animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.25 }} className="included-icon display-5" aria-hidden="true">
                              <TfiPlus />
                            </motion.span>
                          </button>

                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                id={panel}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.35, ease: "easeOut" }}
                                style={{ overflow: "hidden" }}
                              >
                                {section.items
                                  ? <ul className="included-content lead">{section.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
                                  : <p className="included-content included-text lead">{section.text}</p>}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  <div className="included-divider mt-5" />
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* MODAL SLIDESHOW */}
        <Modal show={show} onHide={() => setShow(false)} centered size="xl">
          <Modal.Body className="p-0">
            <Carousel activeIndex={activeIndex} onSelect={(i) => setActiveIndex(i)} indicators interval={3500}>
              {images.map((img, index) => (
                <Carousel.Item key={img + index}>
                  <Picture className="d-block w-100" src={img} alt={`${retreat.name} ${index + 1}`} sizes="90vw" />
                </Carousel.Item>
              ))}
            </Carousel>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default ProductDetail;
